import { useEffect, useRef } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import useSWR from 'swr'

import { trackAction } from '@features/my-gousto-page'
import {
  DASHBOARD_EXPERIENCE_STATE,
  fetchDashboardExperience,
} from '@library/api-customer-engagement-experience'
import type { DashboardSection } from '@library/api-customer-engagement-experience'

export const GOUSTO_ANYTIME_DASHBOARD_FLAG = 'tomatoes_ofx_webclient_gousto_anytime'
export const GOUSTO_ANYTIME_DASHBOARD_SWR_KEY = 'gousto-anytime-dashboard'

type UseGoustoAnytimeDashboardResult = {
  isEnabled: boolean
  isLoading: boolean
  sections: DashboardSection[] | null
  // Server-declared empty-state dashboard, read from the `state` field so gating never touches
  // analytics data.
  isEmptyState: boolean
}

export function useGoustoAnytimeDashboard(): UseGoustoAnytimeDashboardResult {
  const [decision] = useDecision(GOUSTO_ANYTIME_DASHBOARD_FLAG)

  const { data, error } = useSWR(
    decision.enabled ? GOUSTO_ANYTIME_DASHBOARD_SWR_KEY : null,
    fetchDashboardExperience,
  )

  const sections = data?.status === 200 ? data.data?.sections ?? null : null
  const isEmptyState =
    data?.status === 200 && data.data?.state === DASHBOARD_EXPERIENCE_STATE.noOrder
  // On a fetch error (e.g. 5XX) we stop reporting loading so the page falls back to
  // the control view rather than spinning forever.
  const isLoading = decision.enabled && data === undefined && !error

  const pageLoad = data?.status === 200 ? data.pageLoad : undefined

  // Fire the server-supplied page-load event once per mount. SWR revalidates after
  // skip/save mutations, so the ref guard stops the event re-firing on every refetch.
  const hasTrackedPageLoad = useRef(false)
  useEffect(() => {
    if (pageLoad && !hasTrackedPageLoad.current) {
      hasTrackedPageLoad.current = true
      trackAction(pageLoad)
    }
  }, [pageLoad])

  return { isEnabled: decision.enabled, isLoading, sections, isEmptyState }
}
